import { ReactNode } from "react";
import Icon from "../../../base/components/Icon";
import { cx } from "../../utils/cx";
import { IconButton } from "../IconButton";
import { Select } from "../Select";
import styles from "./TableFooter.module.scss";

export interface TableFooterProps {
    className?: string;
    page: number;
    totalPages: number;
    onPageChange?: (page: number) => void;
    /** Current number of rows shown per page. */
    pageSize?: number;
    /** Available page-size choices. */
    pageSizeOptions?: number[];
    /** Called when the page-size control changes. */
    onPageSizeChange?: (pageSize: number) => void;
    /** Total number of rows before pagination. Enables the built-in summary. */
    totalRows?: number;
    leftSlot?: ReactNode;
}

const PrevIcon = () => <Icon icon="arrow_left" />;
const NextIcon = () => <Icon icon="arrow_right" />;

const buildPages = (page: number, totalPages: number): (number | "dots")[] => {
    if (totalPages <= 7)
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, "dots", totalPages];
    if (page >= totalPages - 3) {
        return [
            1,
            "dots",
            totalPages - 4,
            totalPages - 3,
            totalPages - 2,
            totalPages - 1,
            totalPages,
        ];
    }
    return [1, "dots", page - 1, page, page + 1, "dots", totalPages];
};

/** Numbered pagination footer from `TableFooter 🚧 (Beta)`. */
export function TableFooter({
    className,
    page,
    totalPages,
    onPageChange,
    pageSize,
    pageSizeOptions = [10, 25, 50, 75, 100],
    onPageSizeChange,
    totalRows,
    leftSlot,
}: TableFooterProps) {
    const current = Math.min(Math.max(page, 1), Math.max(totalPages, 1));
    const pages = buildPages(current, totalPages);
    const hasRowsSummary = pageSize != null && totalRows != null;
    const firstRow =
        totalRows === 0 || pageSize == null ? 0 : (current - 1) * pageSize + 1;
    const lastRow =
        pageSize == null || totalRows == null
            ? 0
            : Math.min(current * pageSize, totalRows);
    const pageSizeSelectOptions = pageSizeOptions.map(size => ({
        value: String(size),
        label: String(size),
    }));

    return (
        <div className={cx(styles.root, className)} data-ui="table-footer">
            {hasRowsSummary || leftSlot ? (
                <div className={styles.left}>
                    {hasRowsSummary ? (
                        <div className={styles.rowsSummary}>
                            <span className={styles.pageSizeLabel}>
                                Rows per page:
                            </span>
                            <Select
                                className={styles.pageSizeSelect}
                                density="compact"
                                label="Rows per page"
                                onValueChange={value =>
                                    onPageSizeChange?.(Number(value))
                                }
                                options={pageSizeSelectOptions}
                                value={String(pageSize)}
                            />
                            <span aria-live="polite" className={styles.range}>
                                Showing rows {firstRow} to {lastRow} of{" "}
                                {totalRows}
                            </span>
                        </div>
                    ) : (
                        leftSlot
                    )}
                </div>
            ) : null}
            <div className={styles.right}>
                <IconButton
                    aria-label="Previous page"
                    disabled={current <= 1}
                    onClick={() => onPageChange?.(current - 1)}
                    size="sm"
                    variant="tertiary"
                >
                    <PrevIcon />
                </IconButton>
                <div className={styles.pages}>
                    {pages.map((p, index) =>
                        p === "dots" ? (
                            <span className={styles.dots} key={`dots-${index}`}>
                                ...
                            </span>
                        ) : (
                            <button
                                className={styles.pageBtn}
                                data-active={p === current || undefined}
                                key={p}
                                onClick={() => onPageChange?.(p)}
                                type="button"
                            >
                                {p}
                            </button>
                        ),
                    )}
                </div>
                <IconButton
                    aria-label="Next page"
                    disabled={current >= totalPages}
                    onClick={() => onPageChange?.(current + 1)}
                    size="sm"
                    variant="tertiary"
                >
                    <NextIcon />
                </IconButton>
            </div>
        </div>
    );
}

TableFooter.displayName = "TableFooter";
